/**
 * @file billPoReference.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-14
 * @modified 2026-10-14
 * @description An uploaded invoice that quotes one of our POs which is
 *              ALREADY billed for its goods (Cam, 2026-10-14) — a stock
 *              delivery quoted on a later invoice, or an old PO number reused.
 *              Add Bill asks before linking it again: take the link off and
 *              review the bill, or take it off and keep the PO number as the
 *              bill's PO Reference, marked Stock. Bulk Add Bills' summary
 *              sends such a bill to review instead of accepting it.
 *
 *              The PO the reader matched (POST /api/bills/parse-pdf
 *              `suggested.matched_po`) carries its `status` and `billed_by`
 *              (the live bills linked for its goods —
 *              services/billPdfReadService).
 *
 *              1.1.0 (2026-10-14, Cam): "Remove the PO link" reads the bill
 *              as if no PO had been quoted — no supplier fallback to the
 *              PO's, the unbiased supplier match and the lines' own words
 *              (withoutPoLink, from the reader's `without_po` and
 *              `description_without_po`).
 *
 *              Pure.
 */

import { formatDate } from './dateFormatters';

/** The bill's own words for a bill: its supplier invoice number, else its number. */
const billLabel = (b) => b?.supplier_reference || b?.bill_number || (b?.id ? `#${b.id}` : 'another bill');

/**
 * Is the matched PO already billed for its goods by ANOTHER bill (not the
 * one being edited)? Null when it is not (or no PO was matched); else
 * { po, bills } — `bills` the other live goods bills, oldest first (empty
 * when the PO is marked BILLED with no bill linked here, e.g. billed in Xero).
 *
 * @param {object|null} matchedPo
 * @param {{ billId?: number|string|null }} [opts]
 */
export function poBilledElsewhere(matchedPo, { billId = null } = {}) {
  if (!matchedPo || !matchedPo.id) return null;
  const self = billId != null && billId !== '' ? Number(billId) : null;
  const all = Array.isArray(matchedPo.billed_by) ? matchedPo.billed_by : [];
  const others = all.filter(b => Number(b?.id) !== self);
  if (others.length) return { po: matchedPo, bills: others };
  // Marked BILLED with no goods bill here but this one: billed elsewhere
  // (Xero) — unless THIS bill is the one billing it.
  const billed = String(matchedPo.status || '').toUpperCase() === 'BILLED';
  if (billed && !all.some(b => Number(b?.id) === self)) return { po: matchedPo, bills: [] };
  return null;
}

/** "PO-26557 is already billed (Bill 0808140548, 05/10/2026)." */
export function billedPoSentence(billed) {
  if (!billed) return '';
  const number = billed.po?.purchase_order_number || 'The PO';
  if (!billed.bills.length) return `${number} is already billed.`;
  const which = billed.bills
    .map(b => `Bill ${billLabel(b)}${b.bill_date ? `, ${formatDate(b.bill_date)}` : ''}`)
    .join('; ');
  return `${number} is already billed (${which}).`;
}

/** The two choices, in the words the dialog and its buttons use. */
export const BILLED_PO_CHOICES = Object.freeze({
  review: { icon: 'link_off', label: 'Remove the PO link and review the bill' },
  stock: { icon: 'inventory_2', label: 'Remove the PO link, keep the PO number as the PO Reference — mark as Stock' }
});

/**
 * Why Bulk Add Bills' summary will not accept a bill quoting an already
 * billed PO (it is reviewed in Add Bill, which asks), or null.
 */
export function billedPoReviewReason(matchedPo) {
  const billed = poBilledElsewhere(matchedPo);
  return billed
    ? `${billedPoSentence(billed)} Review it: take the PO link off, or keep the number as the PO Reference and mark it Stock.`
    : null;
}

/**
 * The read as if no PO had been quoted (Cam, 2026-10-14 — "read everything
 * from the scanned bill … and forget the PO it found"): no PO linked and none
 * of its lines loaded, no supplier fallback to the PO's, the supplier match
 * and the PO each job quotes worked out without leaning to the PO's supplier
 * (the reader's `without_po`), and every line in the invoice's / item's own
 * words (`description_without_po`) rather than the PO line's. Everything else
 * — dates, due date, invoice number, figures — is the scan's as it was.
 */
export function withoutPoLink(read) {
  if (!read || !read.matched_po) return read;
  const { matched_po: _po, without_po: unbiased, ...rest } = read;
  const out = { ...rest, ...(unbiased && typeof unbiased === 'object' ? unbiased : {}), matched_po: null };
  if (Array.isArray(rest.line_items)) {
    out.line_items = rest.line_items.map((l) => {
      if (!l || !Object.prototype.hasOwnProperty.call(l, 'description_without_po')) return l;
      const { description_without_po: own, ...line } = l;
      return { ...line, description: own ?? '' };
    });
  }
  return out;
}
